import React from "react";

// Brand palette
export const C = {
  navy: "#1C4358",
  red: "#9C1E22",
  redHover: "#861A1D",
  sky: "#9BB9CE",
  khaki: "#C4C89B",
  green: "#2F7D5A",
  amber: "#B7791F",
  charcoal: "#252221",
  slate: "#4B5563",
  line: "#E6EDF2",
};

export const scoreColor = (score, label) => {
  if (label === "Not Enough Data") return C.slate;
  if (score >= 85) return C.green;
  if (score >= 60) return C.amber;
  return C.red;
};

const SEV = {
  high: { bg: "rgba(156,30,34,0.10)", c: C.red, label: "High" },
  medium: { bg: "rgba(183,121,31,0.14)", c: C.amber, label: "Medium" },
  low: { bg: "rgba(28,67,88,0.09)", c: C.navy, label: "Low" },
};

export const SeverityChip = ({ severity }) => {
  const s = SEV[severity] || SEV.low;
  return (
    <span data-testid={`severity-chip-${severity}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: s.bg, color: s.c }}>{s.label}</span>
  );
};

export const PriorityBadge = ({ priority }) => {
  const s = SEV[priority] || SEV.low;
  return (
    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md capitalize" style={{ backgroundColor: s.bg, color: s.c }}>
      {priority || "medium"}
    </span>
  );
};

export const LabelPill = ({ label, score }) => {
  const c = scoreColor(score ?? 100, label);
  return (
    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: `${c}1A`, color: c }}>{label}</span>
  );
};

// Circular score ring (SVG)
export const ScoreRing = ({ score = 0, label = "", size = 116, stroke = 10, testid = "score-ring" }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const off = circ - (pct / 100) * circ;
  const col = scoreColor(score, label);
  return (
    <div className="relative inline-flex items-center justify-center" data-testid={testid} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.line} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 700ms ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display font-bold" style={{ color: col, fontSize: size * 0.28 }}>{score}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: C.slate }}>/ 100</span>
      </div>
    </div>
  );
};

export const StatCard = ({ label, value, tone = C.navy, hint, testid }) => (
  <div data-testid={testid} className="rounded-[0.9rem] border bg-white p-4" style={{ borderColor: C.line }}>
    <div className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: C.slate }}>{label}</div>
    <div className="mt-1 font-display font-bold text-2xl" style={{ color: tone }}>{value}</div>
    {hint && <div className="text-[12px] mt-0.5" style={{ color: C.slate }}>{hint}</div>}
  </div>
);

export const Panel = ({ title, subtitle, right, children, testid }) => (
  <section data-testid={testid} className="rounded-[0.9rem] border bg-white" style={{ borderColor: C.line }}>
    {(title || right) && (
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b" style={{ borderColor: C.line }}>
        <div>
          {title && <h3 className="font-display font-semibold text-[16px]" style={{ color: C.navy }}>{title}</h3>}
          {subtitle && <p className="text-[12.5px] mt-0.5" style={{ color: C.slate }}>{subtitle}</p>}
        </div>
        {right}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const NotConnectedNote = ({ children }) => (
  <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-[12.5px]"
    style={{ backgroundColor: "rgba(155,185,206,0.16)", color: C.navy }}>
    <span className="mt-0.5 inline-block h-2 w-2 rounded-full shrink-0" style={{ background: C.sky }} />
    <span>{children}</span>
  </div>
);

export const Empty = ({ children = "Nothing to show yet." }) => (
  <div className="py-10 text-center text-[13px]" style={{ color: C.slate }}>{children}</div>
);

// ---- CSV export ----
const csvCell = (v) => {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) v = v.join(" | ");
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const downloadCsv = (filename, rows) => {
  if (!rows || !rows.length) return;
  const headers = Array.from(rows.reduce((set, r) => { Object.keys(r).forEach((k) => set.add(k)); return set; }, new Set()));
  const lines = [headers.join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => csvCell(r[h])).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

// ---- Print / PDF-ready report (opens standalone window) ----
export const openPrintReport = (report, scopeLabel) => {
  if (!report) return;
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const h = report.health || {};
  const is = report.issue_summary || {};
  const recRows = (report.recommendations || []).slice(0, 20)
    .map((r) => `<tr><td>${esc(r.priority)}</td><td>${esc(r.title)}</td><td>${esc(r.detail)}</td><td>${esc(r.url || "")}</td></tr>`).join("");
  const pageRows = (report.pages || [])
    .map((p) => `<tr><td>${esc(p.url)}</td><td>${esc(p.page_type)}</td><td>${esc(p.territory || "")}</td><td style="text-align:center">${p.seo_score}</td><td>${esc(p.seo_label)}</td><td>${esc(p.next_action)}</td></tr>`).join("");
  const localRows = (report.local || [])
    .map((l) => `<tr><td>${esc(l.name)}</td><td>${esc(l.status)}</td><td style="text-align:center">${l.score}</td><td>${esc(l.label)}</td><td>${esc(l.next_action)}</td></tr>`).join("");
  const intRows = (report.integrations || [])
    .map((i) => `<tr><td>${esc(i.name)}</td><td>${i.connected ? "Connected" : "Not connected yet"}</td><td>${esc(i.provides)}</td></tr>`).join("");
  const doc = `<!doctype html><html><head><meta charset="utf-8"><title>SEO Intelligence Report</title>
  <style>
    body{font-family:'Work Sans',Arial,sans-serif;color:#252221;margin:32px;}
    h1{color:#1C4358;font-size:24px;margin:0 0 4px} h2{color:#1C4358;font-size:16px;margin:24px 0 8px;border-bottom:2px solid #E6EDF2;padding-bottom:4px}
    .sub{color:#4B5563;font-size:12px;margin-bottom:16px}
    .cards{display:flex;gap:12px;flex-wrap:wrap;margin:12px 0}
    .card{border:1px solid #E6EDF2;border-radius:10px;padding:10px 14px;min-width:120px}
    .card b{display:block;font-size:22px;color:#1C4358} .card span{font-size:11px;color:#4B5563;text-transform:uppercase}
    table{width:100%;border-collapse:collapse;font-size:11.5px;margin-top:6px}
    th,td{border:1px solid #E6EDF2;padding:6px 8px;text-align:left;vertical-align:top}
    th{background:#F4F8FB;color:#1C4358}
    .note{font-size:11px;color:#4B5563;margin-top:6px}
    @media print{.noprint{display:none}}
  </style></head><body>
  <h1>MovingMyMama — SEO Intelligence Report</h1>
  <div class="sub">${esc(scopeLabel || "")} &middot; Generated ${esc(report.generated_at || "")}</div>
  <div class="cards">
    <div class="card"><b>${h.score ?? 0}</b><span>Health / 100</span></div>
    <div class="card"><b>${esc(h.label || "")}</b><span>Overall</span></div>
    <div class="card"><b>${h.pages_total ?? 0}</b><span>Pages audited</span></div>
    <div class="card"><b>${is.high ?? 0}</b><span>High issues</span></div>
    <div class="card"><b>${is.medium ?? 0}</b><span>Medium</span></div>
    <div class="card"><b>${is.low ?? 0}</b><span>Low</span></div>
  </div>
  <div class="note">Native SEO readiness score — NOT a Google ranking score. No rankings, traffic, search volume or backlinks are estimated.</div>
  <h2>Top Recommendations</h2><table><tr><th>Priority</th><th>Title</th><th>Fix</th><th>Page</th></tr>${recRows || "<tr><td colspan=4>None</td></tr>"}</table>
  <h2>Page Audit</h2><table><tr><th>URL</th><th>Type</th><th>Territory</th><th>Score</th><th>Status</th><th>Next action</th></tr>${pageRows || "<tr><td colspan=6>None</td></tr>"}</table>
  <h2>Local SEO Scorecards</h2><table><tr><th>Territory</th><th>Status</th><th>Score</th><th>Label</th><th>Next action</th></tr>${localRows || "<tr><td colspan=5>None</td></tr>"}</table>
  <h2>External Integrations</h2><table><tr><th>Integration</th><th>Status</th><th>Provides</th></tr>${intRows}</table>
  <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.open(); w.document.write(doc); w.document.close();
};
